/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import { apiClient } from './api';
import G17Client, { formDataConfig } from '../services/G17Client';
import { API } from '../constants/errors';
import { loadUniversalTrackers, reloadUniversalTrackers } from './universalTracker';
import { handleError } from './error';
import { naturalSort } from '../utils';
import { withProfile } from '../utils/files';
import type { AppDispatch, RootState } from '../reducers';
import type { ApiResponse } from '../types/api';
import type { InitiativeData } from '../types/initiative';
import type { InitiativeSuggestion } from '../types/initiativeSuggestion';
import type { MetricGroup } from '../types/metricGroup';
import type { TKeyValue } from '../types/file';
import type { UniversalTrackerPlain } from '../types/universalTracker';
import type { OnboardingModelPlain } from '../types/onboarding';
import type { UserRoles } from '../constants/user';
import type { CustomMetricPayload } from '@features/custom-metrics/types';

type GetState = () => RootState;
type MetricGroupsThunkResult = Promise<unknown> | undefined;

export function loadInitiativeById(initiativeId: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const state = getState().initiative;
    if (state.id === initiativeId) {
      return;
    }
    dispatch({
      type: actionTypes.LOADING_INITIATIVE,
      id: initiativeId
    });
    dispatch({
      type: actionTypes.LOADING_UNIVERSALTRACKERS,
      id: initiativeId
    });

    return apiClient.get<ApiResponse<InitiativeData>>(`initiatives/${initiativeId}`)
      .then((response) => {
        dispatch({
          type: actionTypes.LOADED_INITIATIVE,
          data: response.data.data,
          id: initiativeId
        });
        dispatch(loadUniversalTrackers());
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          return dispatch({
            type: actionTypes.ERROR_INITIATIVE,
            data: undefined,
            id: initiativeId
          });
        }
        handleError(dispatch, actionTypes.ERROR_INITIATIVE)(error);
      })
  }
}

export const reloadInitiative = () => {
  return async (dispatch: AppDispatch, getState: GetState) => {

    const initiativeId = getState().initiative.id;
    if (!initiativeId) {
      return; // Nothing to reload
    }

    return apiClient.get<ApiResponse<InitiativeData>>(`initiatives/${initiativeId}`)
      .then((response) => {
        dispatch({
          type: actionTypes.LOADED_INITIATIVE,
          data: response.data.data,
          id: initiativeId
        });
        dispatch(reloadUniversalTrackers());
      })
      .catch(handleError(dispatch, actionTypes.ERROR_INITIATIVE));
  }
}

export function searchInitiativeCompaniesByName(escapedString = '') {
  return apiClient.get<ApiResponse<InitiativeSuggestion[]>>(`initiatives/search/organization?q=${escapedString}`)
    .then(response => response.data.data)
}

export function searchInitiativeHoldings() {
  return apiClient.get<ApiResponse<InitiativeSuggestion[]>>('initiatives/search/holdings')
    .then(response => response.data.data)
}

export function saveReportingLevelDetails(initiativeId: string, form: TKeyValue) {
  return (dispatch: AppDispatch, _getState: GetState) => {
    return G17Client.patch('/initiatives/' + initiativeId, withProfile(form), formDataConfig)
      .then((result) => {
        // @TODO should not reload initiative state,
        //  as it might be updating something else, like portfolio
        dispatch(reloadInitiative());
        return result;
      }).catch(e => {
        console.log(e);
        throw new Error(e.response ? API.GENERIC : e.message);
      });
  }
}

export const loadCustomMetricGroupsByInitiativeId = (initiativeId: string | undefined) => (
  dispatch: AppDispatch,
  getState: GetState
): MetricGroupsThunkResult => {
  if (!initiativeId) {
    return;
  }

  const customMetricGroups = getState().customMetricGroups;
  if (customMetricGroups.initiativeId === initiativeId) {
    return;
  }

  dispatch({
    type: actionTypes.LOADING_CUSTOM_METRIC_GROUPS,
    initiativeId: initiativeId
  });

  return dispatch(reloadCustomMetricGroupsByInitiativeId(initiativeId));
}

export const reloadCustomMetricGroupsByInitiativeId = (initiativeId: string | undefined) => (
  dispatch: AppDispatch,
  getState: GetState
): MetricGroupsThunkResult => {
  if (!initiativeId) {
    return;
  }

  const customMetricGroups = getState().customMetricGroups;
  if (customMetricGroups.initiativeId !== initiativeId) {
    return dispatch(loadCustomMetricGroupsByInitiativeId(initiativeId));
  }

  return apiClient
    .get<ApiResponse<MetricGroup[]>>(`initiatives/${initiativeId}/metric-groups`)
    .then((response) => {
      dispatch({
        type: actionTypes.LOADED_CUSTOM_METRIC_GROUPS,
        data: response.data.data.sort((a, b) => naturalSort(a.groupName, b.groupName)),
        initiativeId: initiativeId
      });
    })
    .catch(handleError(dispatch, actionTypes.ERROR_CUSTOM_METRIC_GROUPS));
}

export function setVerifiedStatus(initiativeId: string, verified = false) {
  return apiClient.patch(`/initiatives/${initiativeId}/verified/${verified ? 'true' : 'false'}`)
    .then((result) => {
      return result;
    }).catch(e => {
      console.log(e);
      throw new Error(e.response ? API.GENERIC : e.message);
    });
}

export function setPremium(initiativeId: string) {
  return apiClient.patch(`/initiatives/${initiativeId}/premium`)
}

// Custom Metrics

export const createMetricGroup = (initiativeId: string, formData: unknown) => G17Client
  .post<ApiResponse<MetricGroup>>(`initiatives/${initiativeId}/metric-groups`, formData, formDataConfig)
  .then((response) => response.data.data);

export const updateMetricGroup = (initiativeId: string, groupId: string, formData: unknown) => G17Client
  .patch<ApiResponse<MetricGroup>>(`initiatives/${initiativeId}/metric-groups/${groupId}`, formData, formDataConfig)
  .then((response) => response.data.data);

export const deleteMetricGroup = (initiativeId: string, groupId: string) => apiClient
  .delete<ApiResponse>(`initiatives/${initiativeId}/metric-groups/${groupId}`)
  .then((response) => response.data.data);


export const addCompanyShareToMetricGroup = (initiativeId: string, groupId: string, shareId: string) => apiClient
  .patch<ApiResponse>(`initiatives/${initiativeId}/metric-groups/${groupId}/share/add`, { initiativeId: shareId })
  .then((response) => response.data.data);

export const removeCompanyShareFromMetricGroup = (initiativeId: string, groupId: string, shareId: string) => apiClient
  .patch<ApiResponse>(`initiatives/${initiativeId}/metric-groups/${groupId}/share/remove`, { initiativeId: shareId })
  .then((response) => response.data.data);

export const addMetricToGroup = (initiativeId: string, groupId: string, utrId: string) => apiClient
  .post<ApiResponse>(`initiatives/${initiativeId}/metric-groups/${groupId}/universal-tracker/${utrId}`)
  .then((response) => response.data.data);

export const removeMetricFromGroup = (initiativeId: string, groupId: string, utrId: string) => apiClient
  .delete<ApiResponse>(`initiatives/${initiativeId}/metric-groups/${groupId}/universal-tracker/${utrId}`)
  .then((response) => response.data.data);

/**
 * @deprecated Use `useCreateCustomMetricMutation` from '@/api/custom-metrics' instead
 * Legacy method - prefer RTK Query mutation for new code
 */
export const createMetric = (initiativeId: string, formData: CustomMetricPayload) => apiClient
  .post<ApiResponse<UniversalTrackerPlain>>(`initiatives/${initiativeId}/metric`, formData)
  .then((response) => response.data.data);

/**
 * @deprecated Use `useUpdateCustomMetricMutation` from '@/api/custom-metrics' instead
 * Legacy method - prefer RTK Query mutation for new code
 */
export const updateMetric = (initiativeId: string, utrId: string, formData: CustomMetricPayload) => apiClient
  .patch<ApiResponse<UniversalTrackerPlain>>(`initiatives/${initiativeId}/metric/${utrId}`, formData)
  .then((response) => response.data.data);

/**
 * @deprecated Use `useDeleteCustomMetricMutation` from '@/api/custom-metrics' instead
 * Legacy method - prefer RTK Query mutation for new code
 */
export const deleteMetric = (initiativeId: string, utrId: string) => apiClient
  .delete<ApiResponse>(`initiatives/${initiativeId}/metric/${utrId}`)
  .then((response) => response.data.data);

// User Management

export const updateUserPermission = (initiativeId: string, userId: string, userRoles: UserRoles[]) => apiClient
  .patch<ApiResponse>(`initiatives/${initiativeId}/users/${userId}`, { permissions: userRoles })
  .then((response) => response.data.data);

export const updateOwner = (initiativeId: string, userId: string) => apiClient
  .patch<ApiResponse>(`initiatives/${initiativeId}/users/${userId}/owner`)
  .then((response) => response.data.data);

export const createMultipleOnboarding = (
  initiativeId: string,
  emails: string[],
  userRoles: UserRoles[],
  expirationDate: Date | undefined
) =>
  apiClient
    .post<ApiResponse<OnboardingModelPlain[]>>(`/initiatives/${initiativeId}/users/onboard`, {
      emails,
      permissions: userRoles,
      expirationDate,
    })
    .then((response) => response.data.data);
